import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Query,
  compose,
  graphql,
  withApollo,
} from 'react-apollo';
import { isEqual } from 'lodash';
import { format } from 'date-fns';

import { livePrice } from '../Price/PriceCampsiteQuery';

// Utils
import getCampsiteType, { getCampsiteTypeDictionaryKey } from '../../lib/campsiteTypes';
import updateRouterQuery, { parseQuery } from '../../lib/updateRouterQuery';
import { checkOpen } from '../../lib/helpers/availability';

import InfoWindowLoading from './InfoWindowLoading';
import InfoWindowPrices from './InfoWindowPrices';
import { LoadingSpinner } from '../ui/Loading';

import withRef from '../../hocs/withRef';
import { dictionaryItem, getDictionaryItem } from '../../hocs/withDictionary';
import { DATE_FORMAT_DISPLAY } from '../../config/locale';

// Get queries
import GET_CAMPSITE_PRICE from '../../config/graphql/getCampsitePrice';
import GET_CAMPSITE, { getCampsiteOptions } from '../../config/graphql/getCampsite';
import GET_PIN, { getPinOptions } from './graphql/getPin';
import GET_POI, { getPoiOptions } from './graphql/getPoi';

import { navigateTo, POST_TYPES } from '../../lib/helpers/navigation';

// Styles
import StyledInfoWindow, {
  StyledErrorMessage,
  CampsiteHeader,
  CampsiteImg,
  CampsiteTitle,
  CampsiteContent,
  Category,
  Footer,
  FromToDate,
  Image,
  LowAvailability,
  MoreDetails,
  ReviewButton,
  Img,
  InfoHeader,
  BasedOnText,
  OffersWrapper,
  OffersIcon,
  OffersText,
  CampsiteCloseWrapper,
  InfoWindowButtonWrapper,
} from './InfoWindow.style';
import { Price, ToursPrice } from './InfoWindowPrices.style';
import { Icon, Text } from '../ui';

import closeSvg from '../../static/images/icons/Close.svg';

import svgOffer from '../../static/images/icons/Offer.svg';
import IbePropTypes from '../../IbePropTypes';
import { ids } from '../../config/campsiteTypes';
import routes from '../../constants/routes';
import formatPrice from '../../lib/formatPrice';
import { SINGLE_EVENT_TYPE } from '../../config/eventTypes';
import { CAMPSITE_SEARCH_TRIGGER_IN_SESSION } from '../../lib/constants';
import { campsiteImageMapInfoKeys, mapAlternativeImages } from '../Picture/pictureHelper';
import Picture from '../Picture';
import GET_SITES_CONFIG from '../../config/graphql/getSitesConfig';
import GET_CONFIGURATION from '../../config/graphql/getConfiguration';
import Note from '../Note';
import ListingRating from '../Listing/ListingRating';
import { partyConfigToPartyString } from '../Guests/guestHelpers';
import products from '../../config/products';

class InfoWindow extends Component {
  static propTypes = {
    campsiteId: PropTypes.string.isRequired,
    campsiteName: PropTypes.string,
    campsiteData: PropTypes.shape({
      campsite: PropTypes.shape(IbePropTypes.campsite),
    }),
    data: PropTypes.shape({
      error: PropTypes.shape({}),
      loading: PropTypes.bool,
      pinInfo: PropTypes.shape(IbePropTypes.pin),
    }),
    hasOffers: PropTypes.bool,
    isPoi: PropTypes.bool,
    lat: PropTypes.number,
    lng: PropTypes.number,
    onCampsiteSearch: PropTypes.func.isRequired,
    pois: PropTypes.shape({
      poi: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.poi)),
    }),
    router: PropTypes.shape(IbePropTypes.router),
    type: PropTypes.number,
    zoomLevel: PropTypes.number,
    sitesConfig: PropTypes.shape(IbePropTypes.sitesConfig).isRequired,
    isSitesConfigLoading: PropTypes.bool.isRequired,
    memberPrice: PropTypes.number,
    standardPrice: PropTypes.number,
    selectedEvent: PropTypes.shape(IbePropTypes.event),
    client: PropTypes.shape(IbePropTypes.client),
    tour: PropTypes.shape(IbePropTypes.tour),
    isTours: PropTypes.bool,
    configuration: PropTypes.shape(IbePropTypes.configuration),
  };

  static defaultProps = {
    campsiteData: {
      campsite: null,
    },
    data: {
      error: null,
      loading: false,
      pinInfo: {
        events: [],
        id: '',
        isoPlaceCode: '',
        name: '',
        numberOfReviews: 0,
        rating: 0,
        type: 0,
      },
    },
    campsiteName: '',
    hasOffers: false,
    isPoi: false,
    lat: null,
    lng: null,
    pois: {
      poi: [],
    },
    router: {
      query: {
        campsiteId: '',
        searchBy: '',
        start: '',
        end: '',
      },
    },
    type: null,
    zoomLevel: null,
    memberPrice: 0,
    standardPrice: 0,
    selectedEvent: null,
    tour: null,
    isTours: false,
    client: null,
    configuration: {},
  };

  state = {
    defaultPartyString: '',
  };

  constructor(props) {
    super(props);

    const { router, configuration } = props;
    const isOverseas = router.query.isOverseas === 'true';

    const productId = isOverseas
      ? products.CAMC_MEMBER_PRODUCT
      : products.CAMC_UK_PRODUCT;

    const product = configuration.products.find((prod) => prod.productCode === productId);

    const stringifiedParty = partyConfigToPartyString(product.partyConfig);

    this.state = {
      defaultPartyString: stringifiedParty?.toLowerCase() ?? '',
    };
  }

  componentDidMount() {
    const activePin = this.props.campsiteId;

    const currentCampsite = {
      id: activePin,
      name: this.props.campsiteName,
      lat: this.props.lat,
      lng: this.props.lng,
      zoomLevel: this.props.zoomLevel,
    };

    // Trigger used set for the below use in SearchWrapper, more documentation is there.
    this.props.onCampsiteSearch(
      currentCampsite,
      window.sessionStorage.getItem(CAMPSITE_SEARCH_TRIGGER_IN_SESSION),
    );

    window.sessionStorage.removeItem(CAMPSITE_SEARCH_TRIGGER_IN_SESSION);
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.router.query.campsiteId !== this.props.router.query.campsiteId) return true;
    if (nextProps.router.query.start !== this.props.router.query.start) return true;
    if (nextProps.router.query.end !== this.props.router.query.end) return true;
    if (nextProps.campsiteId !== this.props.campsiteId) return true;
    if (nextProps.data.error !== this.props.data.error) return true;
    if (nextProps.data.loading !== this.props.data.loading) return true;
    if (!isEqual(nextProps.data.pinInfo, this.props.data.pinInfo)) return true;
    if (nextProps.memberPrice !== this.props.memberPrice) return true;
    if (nextProps?.selectedEvent?.id !== this.props.selectedEvent) return true;
    return false;
  }

  handleMoreDetailsClick = (clickEvent) => {
    // If we click on something which shouldn't trigger this function
    if (clickEvent.target.closest('[data-info-window-button]')) return;
    updateRouterQuery(routes.sites, { campsiteId: this.props.campsiteId });
  }

  handleReviewsClick = () => {
    updateRouterQuery(routes.sites, {
      ...this.props.router.query,
      siteCode: null,
      reviews: true,
      campsiteId: this.props.campsiteId,
    });
  };

  renderImage = (galleryItem, alt) => {
    if (!galleryItem) {
      return <></>;
    }

    const galleryImage = mapAlternativeImages(
      galleryItem.url,
      galleryItem.alternativeFormats,
      campsiteImageMapInfoKeys,
    );

    if (!galleryImage) {
      // maintain compatibility with components that don't utilize new optimized image structure
      return <Img
        alt={alt}
        src={galleryItem || ''}
        loading="eager"
      />;
    }

    return (
      <Picture image={galleryImage}>
        <Img
          alt={alt}
          src={galleryImage.baseUrl || ''}
          loading="eager"
        />
      </Picture>
    );
  }

  handleCloseInfoWindow = () => {
    updateRouterQuery(routes.sites, {
      activePin: null,
    });
  }

  render() {
    if (this.props.data.loading || this.props.isSitesConfigLoading) {
      return (
        <StyledInfoWindow>
          <InfoWindowLoading />
        </StyledInfoWindow>
      );
    }

    if (
      !this.props.isTours &&
      this.props.data.error
      && (!this.props.data.pinInfo || !this.props.data.pinInfo.id)
    ) {
      return (
        <StyledInfoWindow>
          <StyledErrorMessage />
        </StyledInfoWindow>
      );
    }

    const query = parseQuery(this.props.router.query);

    const {
      campsiteId, selectedEvent, isTours, tour, client,
    } = this.props;
    const {
      start, end, eventType, bookingId, componentId,
    } = query;

    if (
      (this.props.data.error && !this.props.isTours)
      || (!this.props.isPoi && this.props.data &&
        !this.props.data.pinInfo && !this.props.isTours)
    ) return null;

    const campsiteType = getCampsiteType(
      this.props.sitesConfig.campsiteTypes,
      this.props.type,
    );

    const poiData = this.props.pois?.poi?.find(
      pointOfInterest => pointOfInterest.id === this.props.campsiteId,
    );

    const isPoi = this.props.isPoi || poiData;

    // pinInfo is only available on a campsite and not a POI
    const campsiteData = this.props.data?.pinInfo || tour;

    const id = isPoi ? poiData?.id : campsiteData?.id;
    const numberOfReviews = isPoi ? null : campsiteData?.numberOfReviews;
    const poiImageGallery = poiData?.image ? [poiData?.image] : [];
    const gallery = isPoi ? poiImageGallery : campsiteData?.gallery;
    const rating = isPoi ? null : campsiteData?.rating;
    const events = campsiteData && campsiteData.events;
    const openDates =
      selectedEvent?.openDates || events?.[0]?.openDates;

    const noPriceCampsiteTypes = [
      ids.UK_AFFILIATED_SITES,
      ids.CERTIFICATED_LOCATIONS,
      ids.OVERSEAS_TOUR,
    ];

    const noRatingCampsiteTypes = [
      ids.UK_AFFILIATED_SITES,
      ids.OVERSEAS_TOUR,
    ];

    const showPrice = noPriceCampsiteTypes.includes(this.props.type) === false;
    const showRating = noRatingCampsiteTypes.includes(this.props.type) === false;
    const eventImage = gallery?.[eventType] || Object.values(gallery ?? {})?.[0];

    const campsiteTypeDictText = getCampsiteTypeDictionaryKey(campsiteType.id);
    const campsiteTypeText = getDictionaryItem(client, `${campsiteTypeDictText}__Text`) ?? campsiteType.text;
    // Regex used to remove the campsite type from the campsite name
    const campsiteTypeRegex = new RegExp(campsiteTypeText, 'gi');
    const name = isPoi
      ? poiData.name
      : campsiteData?.name?.replace(campsiteTypeRegex, '');

    const pricesBasedOnText = getDictionaryItem(client, 'InfoWindow__BasedOn__Text').replace('{{Party}}', this.state.defaultPartyString);

    return (
      <StyledInfoWindow
        color={campsiteType.color}
        key={id}
        id="InfoWindow" // Used to prevent map clicks closing the InfoWindow (Campsitemap.jsx handleMapClick)
      >
        <InfoWindowButtonWrapper onClick={this.handleMoreDetailsClick}>
          <CampsiteHeader>
            {((isPoi || isTours) && gallery?.length > 0) &&
              <CampsiteImg>
                <Image>
                  {this.renderImage(gallery[0], name)}
                </Image>
              </CampsiteImg>
            }
            {!isPoi && !isTours &&
              <CampsiteImg>
                {eventImage && (
                  <Image>
                    {this.renderImage(eventImage, name)}
                  </Image>
                )}
              </CampsiteImg>
            }
            <CampsiteCloseWrapper onClick={this.handleCloseInfoWindow} data-info-window-button>
              <Icon icon={closeSvg} size="12px" />
            </CampsiteCloseWrapper>
            {!isPoi && !isTours &&
              <Note
                backgroundColor={campsiteType?.color}
                color="#FFF"
                text={campsiteTypeText}
                full
              />
            }
            {
              isTours && (
                <Category backgroundColor={campsiteType?.color}>
                  {campsiteType?.text}
                </Category>
              )
            }

            <InfoHeader>
              <CampsiteTitle>
                {name}<br />
                {isTours &&
                  <>
                    {tour?.numberOfNights} {getDictionaryItem(client, 'InfoWindow__ToursNightsSuffix__Text')}
                  </>}
              </CampsiteTitle>
              {(numberOfReviews > 0 && showRating) &&
                <ReviewButton
                  disabled={!numberOfReviews}
                  onClick={this.handleReviewsClick}
                  data-info-window-button
                >
                  <ListingRating
                    rating={rating}
                    numberOfReviews={numberOfReviews}
                  />
                </ReviewButton>
              }
            </InfoHeader>
          </CampsiteHeader>

          {isTours && (
            <ToursPrice>
              <span>{getDictionaryItem(client, 'InfoWindow__ToursPricePrefix__Text')}</span>
              <Price inline>{formatPrice(tour?.fromPrice, undefined, isTours)}</Price>
              <span>{tour?.fromPriceSuffix?.toLowerCase()}</span>
            </ToursPrice>
          )}

          {isPoi && (
            <>
              <FromToDate>
                {format(new Date(poiData.startDateTime), DATE_FORMAT_DISPLAY)}
                {' - '}
                {format(new Date(poiData.endDateTime), DATE_FORMAT_DISPLAY)}
              </FromToDate>
              <CampsiteContent>
                {poiData.promotionalText}
              </CampsiteContent>
              <Footer>
                {poiData.offerTitle && (
                  <>
                    <OffersWrapper>
                      <OffersIcon icon={svgOffer} />
                      <OffersText dictionary={dictionaryItem('InfoWindow', 'OffersAvailable')} />
                    </OffersWrapper>

                    <MoreDetails
                      size="small"
                      hasOffers
                      onClick={() => navigateTo(poiData.poIUrl, {
                        type: POST_TYPES.NEW_WINDOW,
                      })}
                      dictionary={dictionaryItem('InfoWindow', 'ClaimOffer')}
                    />
                  </>
                )}
              </Footer>
            </>
          )}
          {!isPoi && isTours &&
            <Footer>
              <MoreDetails
                marginTop={!showPrice}
                size="small"
                hasOffers={false}
                onClick={this.handleMoreDetailsClick}
                disabled={this.props.campsiteId === this.props.router.query.campsiteId}
                siteDetailsVisible={
                  this.props.campsiteId === this.props.router.query.campsiteId
                }
                dictionary={dictionaryItem('InfoWindow', 'MoreDetails')}
                noPadding
              />
            </Footer>
          }
          {(!isPoi && !isTours &&
            <Query
              query={GET_CAMPSITE_PRICE}
              variables={{
                campsiteId,
                pitchId: (bookingId && componentId) ? componentId : undefined,
                ...livePrice(start, end),
              }}
            >
              {(priceResponse) => {
                if (!priceResponse.data && !this.props.memberPrice) return null;
                const campsitePriceData = priceResponse?.data?.campsitePrice?.data?.[0] ??
                  null;
                const allEventsAreMemberExclusive = campsitePriceData?.events?.every(
                  e => e.isMemberExclusive,
                );
                const isOverseasSite = this.props.type === ids.OVERSEAS_SITE;
                // Find the matched event, if none, select the first
                const eventPrice = campsitePriceData?.events?.find(
                  (event) => event.eventType === Number(eventType) || (
                    isOverseasSite && event.eventType === SINGLE_EVENT_TYPE.id
                  ),
                ) ?? campsitePriceData?.events?.[0]
                  ?? selectedEvent
                  ?? { offers: [], hasLowAvailability: false };

                const offers = eventPrice?.offers ?? [];

                const hasOffers = this.props.hasOffers || !!offers?.length;
                const memberPrice = this.props.memberPrice || eventPrice.cheapestMemberPrice;
                let standardPrice =
                  this.props.standardPrice || eventPrice.cheapestStandardPrice || memberPrice;

                const isMemberExclusive = allEventsAreMemberExclusive
                  || (selectedEvent && selectedEvent.isMemberExclusive);

                if (isMemberExclusive) {
                  standardPrice = null;
                }

                // Get hasLowAvailability from campsite detail endpoint
                const campsiteEvent = this.props.campsiteData?.campsite?.events?.find(
                  (event) => event.eventType === Number(eventType) || (
                    isOverseasSite && event.eventType === SINGLE_EVENT_TYPE.id
                  ),
                ) ?? this.props.campsiteData?.campsite?.events?.[0];

                const hasLowAvailability = campsiteEvent?.hasLowAvailability
                  ?? this.props.campsiteData?.campsite?.hasLowAvailability
                  ?? false;

                const hasPrice = !!memberPrice;

                return (
                  <>
                    {showPrice &&
                      <CampsiteContent>
                        <InfoWindowPrices
                          parentLoading={this.props.data.loading}
                          isOpen={checkOpen(
                            eventPrice?.openDates || openDates,
                            query.start,
                            query.end,
                          )}
                          memberPrice={memberPrice}
                          standardPrice={isOverseasSite ? null : standardPrice}
                          isTours={isTours}
                          campsiteId={this.props.campsiteId}
                          memberExclusive={isMemberExclusive}
                        />
                      </CampsiteContent>
                    }
                    {priceResponse.loading && <LoadingSpinner size="1rem" />}

                    <Footer>
                      {hasPrice &&
                        <BasedOnText>
                          <Text>{pricesBasedOnText}</Text>
                        </BasedOnText>
                      }

                      {hasLowAvailability &&
                        <LowAvailability dictionary={dictionaryItem('InfoWindow', 'LowAvailability')} />
                      }

                      {hasOffers && (
                        <OffersWrapper>
                          <OffersIcon icon={svgOffer} />
                          <OffersText dictionary={dictionaryItem('InfoWindow', 'OffersAvailable')} />
                          {offers.length > 1 && <span>({offers.length})</span>}
                        </OffersWrapper>
                      )}

                      <MoreDetails
                        marginTop={!showPrice}
                        size="small"
                        hasOffers={hasOffers}
                        onClick={this.handleMoreDetailsClick}
                        disabled={this.props.campsiteId === this.props.router.query.campsiteId}
                        siteDetailsVisible={
                          this.props.campsiteId === this.props.router.query.campsiteId
                        }
                        dictionary={dictionaryItem('InfoWindow', 'MoreDetails')}
                      />
                    </Footer>
                  </>
                );
              }}
            </Query>
          )}
        </InfoWindowButtonWrapper>
      </StyledInfoWindow>
    );
  }
}

export default compose(
  withRef,
  withApollo,
  graphql(GET_SITES_CONFIG, {
    props: ({ data }) => ({
      sitesConfig: data.configurationSites,
      isSitesConfigLoading: data.loading,
    }),
  }),
  graphql(GET_CONFIGURATION, {
    props: ({ data }) => ({
      configuration: data.configuration,
    }),
  }),
  graphql(GET_CAMPSITE, { options: getCampsiteOptions, name: 'campsiteData' }), // campsite detail API call
  graphql(GET_PIN, { options: getPinOptions }), // campsite pin API call, if poi this is skipped
  graphql(GET_POI, { options: getPoiOptions, name: 'pois' }), // cached poi data, if campsite this is skipped
)(InfoWindow);
